import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, MaskedLines } from "../components/motion";
import { CtaBanner } from "../components/CtaBanner";
import { SITE } from "../data/vpns";
import { Mail } from "lucide-react";

const Section = ({ title, children }) => (
  <Reveal className="mb-12">
    <h2 className="mb-4 font-display text-2xl font-extrabold tracking-tight text-[#0F172A]">{title}</h2>
    <div className="article-body">{children}</div>
  </Reveal>
);

const DarkHero = ({ eyebrow, lines, sub }) => (
  <section className="grain relative overflow-hidden bg-[#0F172A]">
    <div className="hero-grid absolute inset-0" />
    <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-[#7EAAFF]">{eyebrow}</p>
      <h1 className="font-display text-4xl font-black tracking-tighter text-white sm:text-5xl">
        <MaskedLines lines={lines} />
      </h1>
      {sub && <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">{sub}</p>}
    </div>
  </section>
);

export const About = () => (
  <Layout>
    <Seo
      title="About VPNReviews.ai: Who We Are & How We Test"
      description="VPNReviews.ai is an independent VPN testing publication. Learn who we are, how we test, and why our rankings can't be bought."
    />
    <DarkHero eyebrow="About" lines={["Independent Testing.", "Honest Rankings."]} sub="We exist to cut through VPN marketing with real data." />
    <Breadcrumbs crumbs={[{ label: "About" }]} />
    <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8">
      <Section title="Who We Are">
        <p>
          VPNReviews.ai is an independent editorial publication dedicated to testing and ranking consumer VPN
          services. Our team combines backgrounds in network engineering, cybersecurity, and consumer journalism —
          and we use the products we review every single day.
        </p>
        <p>
          We currently track five leading VPN providers — NordVPN, Surfshark, PureVPN, VeePN, and AdGuard VPN — and
          retest them monthly as pricing, features, and performance change.
        </p>
      </Section>
      <Section title="How We Test">
        <ul>
          <li>Speed: repeated benchmarks across 47 server locations against a fixed base connection</li>
          <li>Streaming: verification against 7 services including 6 Netflix regional libraries, over 6-week windows</li>
          <li>Security: DNS/IPv6 leak tests, kill switch force-drop tests, and protocol inspection</li>
          <li>Privacy: independent audit records (PwC, Deloitte, KPMG) weighted far above marketing claims</li>
          <li>Usability: hands-on scoring of apps across Windows, macOS, iOS, and Android</li>
        </ul>
      </Section>
      <Section title="Editorial Independence">
        <p>
          VPNReviews.ai is reader-supported: we earn affiliate commissions when you purchase through our links, at no
          extra cost to you. Crucially, our rankings are determined by test results before any commercial
          consideration. Every VPN in our lineup pays a broadly similar commission — there is no financial incentive
          for us to prefer one over another. We rank on merit, and we publish the data to prove it.
        </p>
      </Section>
    </div>
    <CtaBanner />
  </Layout>
);

export const Contact = () => (
  <Layout>
    <Seo
      title="Contact VPNReviews.ai: Editorial & Partnership Enquiries"
      description="Get in touch with the VPNReviews.ai editorial team for corrections, testing enquiries, or partnership questions."
    />
    <DarkHero eyebrow="Contact" lines={["Talk to the", "Editorial Team"]} />
    <Breadcrumbs crumbs={[{ label: "Contact" }]} />
    <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8">
      <Reveal>
        <div className="border border-slate-200 p-10" data-testid="contact-card">
          <Mail size={28} className="text-[#4687FF]" />
          <h2 className="mt-5 font-display text-2xl font-extrabold tracking-tight text-[#0F172A]">
            We read everything.
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Corrections, re-test requests, tips, and partnership enquiries — email us and we'll get back to you
            within two business days.
          </p>
          <a
            href={`mailto:${SITE.email}`}
            data-testid="contact-email-link"
            className="mt-6 inline-block font-display text-lg font-bold text-[#4687FF] underline underline-offset-4 hover:text-[#2563eb]"
          >
            {SITE.email}
          </a>
          <p className="mt-8 text-xs leading-relaxed text-slate-400">
            Note: we do not accept payment for placement in our rankings, and we do not remove honest reviews at a
            vendor's request.
          </p>
        </div>
      </Reveal>
    </div>
    <CtaBanner />
  </Layout>
);

export const PrivacyPolicy = () => (
  <Layout>
    <Seo
      title="Privacy Policy | VPNReviews.ai"
      description="How VPNReviews.ai collects, uses, and protects your data — including analytics, cookies, and affiliate tracking."
    />
    <DarkHero eyebrow="Legal" lines={["Privacy Policy"]} sub="Last updated: January 2026" />
    <Breadcrumbs crumbs={[{ label: "Privacy Policy" }]} />
    <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8" data-testid="privacy-policy-body">
      <Section title="1. Introduction">
        <p>
          VPNReviews.ai ("we", "us", "our") operates the website vpnreviews.ai. This Privacy Policy explains what
          information we collect when you visit our site, how we use it, and the choices you have. We are a
          privacy-focused publication, and we practise what we preach: we collect the minimum data necessary to
          operate the site.
        </p>
      </Section>
      <Section title="2. Information We Collect">
        <ul>
          <li>Usage data: pages visited, time on page, referring site, browser type, and device category — collected via Google Analytics 4 in aggregated, pseudonymised form</li>
          <li>Contact data: if you email us, we retain your email address and message to respond to your enquiry</li>
          <li>Cookies: we use essential cookies for site functionality and analytics cookies to understand aggregate traffic patterns</li>
        </ul>
        <p>We do not collect names, payment information, or account data — the site requires no registration.</p>
      </Section>
      <Section title="3. Affiliate Links & Third Parties">
        <p>
          When you click an affiliate link on our site, the destination (a VPN provider or its tracking network) may
          set its own cookies to attribute a potential purchase to us. These third-party cookies are governed by the
          respective provider's privacy policy. We receive only aggregate, anonymised conversion reports — never your
          personal purchase details.
        </p>
      </Section>
      <Section title="4. How We Use Information">
        <ul>
          <li>To operate, maintain, and improve the website</li>
          <li>To understand which content is most useful to readers</li>
          <li>To respond to enquiries sent to our contact email</li>
          <li>To measure aggregate affiliate performance</li>
        </ul>
        <p>We do not sell, rent, or trade personal information. Ever.</p>
      </Section>
      <Section title="5. Data Retention & Security">
        <p>
          Analytics data is retained per Google Analytics' standard retention settings (14 months). Contact emails
          are retained for as long as necessary to handle the enquiry. The site is served exclusively over HTTPS.
        </p>
      </Section>
      <Section title="6. Your Rights">
        <p>
          Depending on your jurisdiction (GDPR, CCPA, and similar), you may have rights to access, correct, or delete
          personal data we hold, and to opt out of analytics. You can block analytics cookies via your browser or
          Google's opt-out tools. To exercise any right, contact us at {SITE.email}.
        </p>
      </Section>
      <Section title="7. Children's Privacy">
        <p>Our site is not directed at children under 16 and we do not knowingly collect their data.</p>
      </Section>
      <Section title="8. Changes to This Policy">
        <p>
          We may update this policy from time to time. Material changes will be reflected by the "Last updated" date
          at the top of this page.
        </p>
      </Section>
      <Section title="9. Contact">
        <p>Questions about this policy: {SITE.email}</p>
      </Section>
    </div>
  </Layout>
);

export const AffiliateDisclosure = () => (
  <Layout>
    <Seo
      title="Affiliate Disclosure | VPNReviews.ai"
      description="FTC-compliant disclosure of VPNReviews.ai's affiliate relationships — how we earn, and why it never affects our rankings."
    />
    <DarkHero eyebrow="Legal" lines={["Affiliate Disclosure"]} sub="Full transparency, as required by the FTC — and by our own standards." />
    <Breadcrumbs crumbs={[{ label: "Affiliate Disclosure" }]} />
    <div className="mx-auto max-w-3xl px-6 py-16 lg:px-8" data-testid="affiliate-disclosure-body">
      <Section title="The Short Version">
        <p>
          VPNReviews.ai is reader-supported. When you click a link to a VPN provider on our site and make a purchase,
          we may earn an affiliate commission — at no additional cost to you. This is how we fund our testing and
          keep the site free to read.
        </p>
      </Section>
      <Section title="Our Affiliate Relationships">
        <p>
          We participate in the affiliate programs of all five VPN providers we currently review: NordVPN, Surfshark,
          PureVPN, VeePN, and AdGuard VPN. Links to these providers on our site are affiliate links and are marked
          with rel="sponsored" attributes in accordance with search engine guidelines.
        </p>
      </Section>
      <Section title="Why This Doesn't Affect Our Rankings">
        <ul>
          <li>All five providers pay broadly similar commission rates — there is no financial reason to favour one</li>
          <li>Rankings are finalised from test data before any commercial consideration</li>
          <li>We publish our raw test results (speed retention, unblocking rates, latency) so you can check our work</li>
          <li>We include criticisms and 'cons' for every product, including our #1 pick</li>
        </ul>
      </Section>
      <Section title="FTC Compliance">
        <p>
          This disclosure is provided in accordance with the United States Federal Trade Commission's 16 CFR Part 255:
          "Guides Concerning the Use of Endorsements and Testimonials in Advertising." If you have any questions about
          our affiliate relationships, contact us at {SITE.email}.
        </p>
      </Section>
    </div>
  </Layout>
);
